/**
 * AdminSupervision — Phase 5 Mission Prioritaire.
 *
 * Console administrateur de supervision des accès, anomalies et activité.
 * Lecture seule sur audit_logs V2. Réservé aux admins.
 */
import { useCallback, useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";
import {
  Activity, AlertTriangle, Globe2, ShieldAlert, Download, Users, RefreshCw, Loader2,
} from "lucide-react";

const HOURS_OPTIONS = [1, 6, 24, 72, 168];

export default function AdminSupervision() {
  const [hours, setHours] = useState(24);
  const [overview, setOverview] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, an] = await Promise.all([
        apiClient.get(`/admin/supervision/overview?hours=${hours}`),
        apiClient.get(`/admin/supervision/anomalies?hours=${hours}`),
      ]);
      setOverview(ov.data);
      setAnomalies(an.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <Navbar variant="light" />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2">Console administrateur</div>
            <h1 className="font-serif text-4xl text-[#0B3A2A]">Supervision & Audit</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Détection d'accès inhabituels, téléchargements massifs, connexions simultanées,
              et tentatives de brute force. Lecture seule — aucun journal ne peut être supprimé.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex border border-slate-300 rounded-full overflow-hidden text-xs" data-testid="window-selector">
              {HOURS_OPTIONS.map((h) => (
                <button
                  key={h}
                  onClick={() => setHours(h)}
                  data-testid={`window-${h}h`}
                  className={`px-3 py-1.5 ${hours === h ? "bg-[#0B3A2A] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                >
                  {h < 24 ? `${h}h` : `${h / 24}j`}
                </button>
              ))}
            </div>
            <Button onClick={refresh} variant="outline" className="rounded-full" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" />Rafraîchir
            </Button>
          </div>
        </div>

        {error && (
          <Card className="p-4 rounded-2xl border-rose-200 bg-rose-50 text-sm text-rose-700 mb-4">{error}</Card>
        )}

        {loading && !overview ? (
          <Card className="p-10 rounded-2xl text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#C9A961]" /></Card>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="supervision-kpis">
              <KpiCard label="Événements" value={overview?.kpi?.total_events} icon={<Activity className="w-4 h-4" />} />
              <KpiCard label="Logins OK" value={overview?.kpi?.logins_success} accent="emerald" />
              <KpiCard label="Logins échoués" value={overview?.kpi?.logins_failed} accent="rose" />
              <KpiCard label="Step-up réussis" value={overview?.kpi?.step_up_success} accent="emerald" />
              <KpiCard label="Step-up refusés" value={overview?.kpi?.step_up_failed} accent="rose" />
              <KpiCard label="Téléchargements" value={overview?.kpi?.downloads} icon={<Download className="w-4 h-4" />} />
              <KpiCard label="Vérificateur débloqué" value={overview?.kpi?.verifier_unlocks} />
              <KpiCard label="Transmissions spécialistes" value={overview?.kpi?.verifier_handoffs} icon={<Users className="w-4 h-4" />} />
            </div>

            {/* Anomalies */}
            <h2 className="font-serif text-2xl text-[#0B3A2A] mb-4 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" /> Anomalies détectées
            </h2>

            <AnomalySection
              title="Téléchargements / consultations massifs (≥ 5)"
              items={anomalies?.mass_downloads}
              testid="anomaly-mass-downloads"
              renderRow={(d, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 pr-3 text-sm">{d.actor}</td>
                  <td className="py-2 pr-3 text-sm font-semibold">{d.count}</td>
                  <td className="py-2 pr-3 text-xs font-mono text-slate-600">{(d.ips || []).join(", ")}</td>
                  <td className="py-2 text-xs text-slate-500">{new Date(d.last_at).toLocaleString("fr-FR")}</td>
                </tr>
              )}
              cols={["Acteur", "Nombre", "IPs", "Dernier"]}
              empty="Aucun téléchargement massif."
            />

            <AnomalySection
              title="Connexions simultanées (≥ 2 IPs)"
              items={anomalies?.simultaneous_sessions}
              testid="anomaly-simultaneous"
              renderRow={(d, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 pr-3 text-sm">{d.actor}</td>
                  <td className="py-2 pr-3 text-sm">{d.ip_count}</td>
                  <td className="py-2 pr-3 text-xs font-mono text-slate-600">{(d.ips || []).join(", ")}</td>
                  <td className="py-2 pr-3 text-xs">{(d.countries || []).join(", ") || "—"}</td>
                  <td className="py-2 text-xs text-slate-500">{new Date(d.last_at).toLocaleString("fr-FR")}</td>
                </tr>
              )}
              cols={["Acteur", "# IPs", "IPs", "Pays", "Dernier"]}
              empty="Aucune session simultanée."
            />

            <AnomalySection
              title="Connexions multi-pays"
              items={anomalies?.multi_country_logins}
              testid="anomaly-multi-country"
              icon={<Globe2 className="w-4 h-4 text-amber-600" />}
              renderRow={(d, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 pr-3 text-sm">{d.actor}</td>
                  <td className="py-2 pr-3 text-xs">{(d.countries || []).join(", ")}</td>
                  <td className="py-2 text-xs text-slate-500">{new Date(d.last_at).toLocaleString("fr-FR")}</td>
                </tr>
              )}
              cols={["Acteur", "Pays", "Dernier"]}
              empty="Aucune connexion multi-pays."
            />

            <AnomalySection
              title="Logins échoués répétés (≥ 5 / heure)"
              items={anomalies?.repeated_failed_logins}
              testid="anomaly-failed-logins"
              icon={<AlertTriangle className="w-4 h-4 text-rose-600" />}
              renderRow={(d, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 pr-3 text-xs font-mono">{d.ip}</td>
                  <td className="py-2 pr-3 text-sm">{d.target_email || "—"}</td>
                  <td className="py-2 pr-3 text-sm font-semibold">{d.attempts}</td>
                  <td className="py-2 text-xs text-slate-500">{new Date(d.last_at).toLocaleString("fr-FR")}</td>
                </tr>
              )}
              cols={["IP", "Cible", "Tentatives", "Dernier"]}
              empty="Aucune attaque détectée."
            />

            <AnomalySection
              title="Step-up brute-force (≥ 3 / 15 min)"
              items={anomalies?.step_up_brute_force}
              testid="anomaly-step-up-brute"
              renderRow={(d, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 pr-3 text-sm">{d.actor}</td>
                  <td className="py-2 pr-3 text-sm font-semibold">{d.attempts}</td>
                  <td className="py-2 text-xs text-slate-500">{new Date(d.last_at).toLocaleString("fr-FR")}</td>
                </tr>
              )}
              cols={["Acteur", "Tentatives", "Dernier"]}
              empty="Aucun brute-force step-up détecté."
            />

            {/* Top pays / actions */}
            <div className="grid md:grid-cols-2 gap-4 mt-8">
              <Card className="p-5 rounded-2xl border-slate-100" data-testid="top-countries">
                <h3 className="font-serif text-lg text-[#0B3A2A] mb-3 inline-flex items-center gap-2">
                  <Globe2 className="w-4 h-4" /> Top pays
                </h3>
                <table className="w-full text-sm">
                  <tbody>
                    {(overview?.top_countries || []).map((c, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-0">
                        <td className="py-1.5">{c.country}</td>
                        <td className="py-1.5 text-right font-medium">{c.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
              <Card className="p-5 rounded-2xl border-slate-100" data-testid="top-actions">
                <h3 className="font-serif text-lg text-[#0B3A2A] mb-3">Top actions</h3>
                <table className="w-full text-sm">
                  <tbody>
                    {(overview?.top_actions || []).map((c, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-0">
                        <td className="py-1.5 font-mono text-xs">{c.action}</td>
                        <td className="py-1.5 text-right font-medium">{c.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

function KpiCard({ label, value, icon, accent }) {
  const accentCls = accent === "emerald" ? "text-emerald-700" : accent === "rose" ? "text-rose-700" : "text-[#0B3A2A]";
  return (
    <Card className="p-4 rounded-2xl border-slate-100">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-1">
        {icon}{label}
      </div>
      <div className={`font-serif text-3xl ${accentCls}`}>{value ?? 0}</div>
    </Card>
  );
}

function AnomalySection({ title, items, renderRow, cols, empty, testid, icon }) {
  const list = items || [];
  return (
    <Card className="p-5 rounded-2xl border-slate-100 mb-4" data-testid={testid}>
      <h3 className="font-serif text-lg text-[#0B3A2A] mb-3 flex items-center gap-2">
        {icon || <AlertTriangle className="w-4 h-4 text-amber-600" />}
        {title}
        <Badge className="bg-slate-100 text-slate-700 rounded-full text-xs ml-1">{list.length}</Badge>
      </h3>
      {list.length === 0 ? (
        <p className="text-sm text-slate-500 italic">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                {cols.map((c) => <th key={c} className="py-2 pr-3 font-medium">{c}</th>)}
              </tr>
            </thead>
            <tbody>{list.map(renderRow)}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
